import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import AgalliaoLogo from './AgalliaoLogo';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Account for fixed navbar height
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMenuOpen(false); // Close mobile menu after clicking
  };

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  const navLinks = [
    { name: 'Advisory', id: 'advisory' },
    { name: 'Services', id: 'services' },
    { name: 'Process', id: 'process' },
  ];

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 flex justify-center p-4">
        <div className="w-full max-w-[1440px] flex items-center justify-between px-6 md:px-8 py-4 bg-[#0a1220]/80 backdrop-blur-xl text-white rounded-none transition-all duration-300">
          {/* Main Logo */}
          <div 
            className="flex items-center group cursor-pointer shrink-0"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <AgalliaoLogo className="h-8 md:h-10 w-auto group-hover:opacity-80 transition-all" />
          </div>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <a 
                key={link.id}
                href={`#${link.id}`} 
                onClick={(e) => scrollToSection(e, link.id)}
                className="text-[13px] font-bold uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors relative group"
              >
                {link.name}
                <span className="absolute -bottom-1 left-0 w-0 h-[1.5px] bg-[#C5A059] transition-all group-hover:w-full"></span>
              </a>
            ))}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-6">
            {/* Desktop Contact Button */}
            <a 
              href="https://wa.me/27839287731"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:block bg-[#C5A059] text-zinc-950 px-6 py-2.5 rounded-none text-[13px] font-bold uppercase tracking-widest hover:bg-[#b08c49] transition-all border-none whitespace-nowrap"
            >
              Contact Us
            </a>

            {/* Mobile Hamburger Toggle */}
            <button 
              onClick={toggleMenu}
              className="lg:hidden p-2 text-zinc-400 hover:text-white transition-colors focus:outline-none"
              aria-label="Toggle mobile menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 z-[60] bg-[#020712]/98 backdrop-blur-2xl transition-all duration-700 ease-in-out lg:hidden ${
          isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
        }`}
      >
        {/* Mobile Menu Top Logo - Centered */}
        <div className="absolute top-0 left-0 right-0 p-12 flex items-center justify-center">
          <AgalliaoLogo className="h-9 w-auto" />
        </div>

        {/* Menu Content */}
        <div className="flex flex-col items-center justify-center h-full gap-10 px-8 pt-16">
          {navLinks.map((link, idx) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              onClick={(e) => scrollToSection(e, link.id)}
              className={`text-3xl font-black uppercase tracking-[0.4em] transition-all duration-700 ${
                isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } text-zinc-500 hover:text-white`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              {link.name}
            </a>
          ))}
          
          <div className="w-full flex flex-col items-center gap-12 mt-4 text-center">
            <a 
              href="https://wa.me/27839287731"
              target="_blank"
              rel="noopener noreferrer"
              className={`bg-[#C5A059] text-zinc-950 px-10 py-5 rounded-none font-black text-[15px] uppercase tracking-[0.3em] transition-all duration-700 hover:bg-[#b08c49] ${
                isMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } w-full max-w-sm`}
              style={{ transitionDelay: '300ms' }}
            >
              Contact Us
            </a>

            {/* Close Icon below the button */}
            <button 
              onClick={toggleMenu}
              className={`p-4 text-zinc-500 hover:text-white transition-all duration-1000 transform ${
                isMenuOpen ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-50 rotate-90'
              } focus:outline-none`}
              aria-label="Close mobile menu"
            >
              <X className="w-10 h-10" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;